'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { SESSION_COOKIE_KEY } from '@/constants/session'
import { INPUT_TYPES } from '@/constants/utils'
import { guestSignIn, verifyGuestOtp, resendGuestOtp } from '@/api/guest/auth'
import { guestLoginWithOtpSchema, type GuestLoginWithOtpSchema } from '@/lib/schemas/guestAuth'
import { updateSession } from '@/lib/session'
import { useTimer } from '@/lib/hooks/useTimer'
import OTPField from '@/components/OTPField'
import ControllerField from '@/components/HookForm/components/ControllerField'
import Button from '@/components/livecontact/Button'
import { Collapsible } from '@/components/livecontact/Collapsible'
import LcIcon from '@/components/livecontact/icons'
import OtpPopStyle, { OTP_CELL_CLASS } from '@/templates/SignInPage/components/OtpPopStyle'
import ResendCodeLink from '@/templates/SignInPage/components/ResendCodeLink'
import StepSwap from '@/templates/SignInPage/components/StepSwap'

/** Sign-in form: credentials, then a 6-digit code. Both steps live in the same card. */
const SignIn = () => {
  const [step, setStep] = useState<'credentials' | 'otp'>('credentials')
  const [resendLoading, setResendLoading] = useState(false)
  const [cooldown, startCooldown] = useTimer(60)
  const router = useRouter()

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isSubmitting },
  } = useForm<GuestLoginWithOtpSchema>({
    reValidateMode: 'onBlur',
    resolver: zodResolver(guestLoginWithOtpSchema),
    defaultValues: { email: '', password: '', code: '' },
  })

  const handleResend = async () => {
    setResendLoading(true)
    try {
      const formData = getValues()
      await resendGuestOtp(formData)
      startCooldown()
    } finally {
      setResendLoading(false)
    }
  }

  const onSubmit = async (data: GuestLoginWithOtpSchema) => {
    if (step === 'credentials') {
      await guestSignIn({ email: data.email, password: data.password })
      setStep('otp')
    } else {
      const { token } = await verifyGuestOtp({ email: data.email, code: data.code || '' })
      if (token) {
        await updateSession(token, SESSION_COOKIE_KEY)
        router.push('/panel/_/contact-cards')
      }
    }
  }

  const isOtp = step === 'otp'

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <StepSwap stepKey={step} direction={isOtp ? 1 : -1}>
        {isOtp ? (
          <div className="flex flex-col gap-3">
            <OtpPopStyle />
            <Controller
              name="code"
              control={control}
              render={({ field, fieldState }) => (
                <OTPField
                  classInput={`grow h-10 ${OTP_CELL_CLASS}`}
                  length={6}
                  value={field.value || ''}
                  onChange={field.onChange}
                  onComplete={() => handleSubmit(onSubmit)()}
                  autoFocus
                  label="Verification code"
                  note={`We sent a 6-digit code to ${getValues('email')}`}
                  error={fieldState.error}
                />
              )}
            />
            <ResendCodeLink onResend={handleResend} cooldown={cooldown} loading={resendLoading} />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <ControllerField
              name="email"
              componentKey={INPUT_TYPES.FIELD}
              control={control}
              classInput="h-10"
              placeholder="you@business.com"
              label="Email"
              icon="email"
              type="email"
              required
            />

            <div className="flex flex-col gap-2">
              <ControllerField
                name="password"
                componentKey={INPUT_TYPES.FIELD}
                control={control}
                classInput="h-10 pr-10"
                placeholder="Password"
                label="Password"
                icon="lock"
                type="password"
                required
              />
              <Link
                href="/forgot-password"
                className="w-fit rounded-[6px] text-[12px] font-medium tracking-[-0.2px] text-brand underline-offset-4 outline-none transition-colors duration-[140ms] hover:underline focus-visible:ring-2 focus-visible:ring-brand/40"
              >
                Forgot password?
              </Link>
            </div>
          </div>
        )}
      </StepSwap>

      <Button
        className="mt-1 w-full"
        size="lg"
        type="submit"
        loading={isSubmitting}
        trailingIcon={<LcIcon name="arrow-right" size={14} />}
      >
        {isOtp ? 'Verify code' : 'Continue'}
      </Button>

      <Collapsible open={isOtp}>
        <Button
          type="button"
          variant="ghost"
          className="w-full text-ink-3"
          onClick={() => setStep('credentials')}
          disabled={isSubmitting}
        >
          Use a different account
        </Button>
      </Collapsible>

      {/* Sign-up is a different flow (multi-step onboarding), not a peer state of this card, so
          it reads as a footer rather than a tab. It folds away at the code step, where the only
          sensible moves are "verify" and "go back". */}
      <Collapsible open={!isOtp}>
        <div className="flex flex-col items-center gap-3 border-t border-line pt-4">
          <p className="text-[13px] leading-[1.4] tracking-[-0.2px] text-ink-4">
            Don&apos;t have an account yet?
          </p>
          <Button asChild variant="secondary" size="lg" className="w-full">
            <Link href="/">Create an account</Link>
          </Button>
        </div>
      </Collapsible>
    </form>
  )
}

export default SignIn
